import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import Layout from './Layout';
import { useAuth } from '../context/AuthContext';
import './CustomersImport.css';

const CustomersImport = () => {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const inputRef = useRef(null);
  const dragCounterRef = useRef(0);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const applyExcelFile = useCallback((selected) => {
    if (!selected) return;
    const name = (selected.name || '').toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.xls')) {
      setError('Please select an Excel file (.xlsx or .xls).');
      setFile(null);
      setFileName('');
      return;
    }
    setFile(selected);
    setFileName(selected.name);
    setError('');
    setResult(null);
  }, []);

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    applyExcelFile(selected);
    e.target.value = '';
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e) => {
    handleDrag(e);
    dragCounterRef.current += 1;
    if (e.dataTransfer?.types?.includes('Files')) setDragActive(true);
  };

  const handleDragOut = (e) => {
    handleDrag(e);
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    handleDrag(e);
    dragCounterRef.current = 0;
    setDragActive(false);
    const dropped = e.dataTransfer.files?.[0];
    applyExcelFile(dropped);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select an Excel file.');
      return;
    }
    setIsUploading(true);
    setError('');
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = getToken();
      const res = await fetch('/api/customers/import', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      const raw = await res.text();
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        data = null;
      }
      if (!res.ok) {
        const msg =
          (data && data.message) ||
          raw ||
          'Import failed';
        throw new Error(msg);
      }
      if (!data) throw new Error('Import succeeded but server returned an unexpected response.');
      setResult(data);
      setFile(null);
      setFileName('');
    } catch (err) {
      setError(err.message || 'Failed to import customers.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Layout>
      <div className="customers-import">
        <div className="customers-import-head">
          <Button
            type="button"
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/customers')}
            sx={{ alignSelf: 'flex-start', mb: 1, textTransform: 'none', fontWeight: 600 }}
          >
            Back
          </Button>
          <h1 className="home-menu-title customers-import-title">Import Customers</h1>
          <p className="home-menu-intro customers-import-intro">
            Upload an Excel file with columns: CUST NO, CAT, CUSTOMER NAME.
          </p>
        </div>

        <div className="customers-import-panel">
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            className="customers-import-input-hidden"
            onChange={handleFileChange}
            disabled={isUploading}
            aria-hidden
          />
          <div
            className={`customers-import-dropzone${dragActive ? ' customers-import-dropzone--active' : ''}${
              isUploading ? ' customers-import-dropzone--disabled' : ''
            }`}
            role="button"
            tabIndex={0}
            onClick={() => !isUploading && inputRef.current?.click()}
            onKeyDown={(e) => {
              if (!isUploading && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
            onDragEnter={handleDragIn}
            onDragLeave={handleDragOut}
            onDragOver={handleDragIn}
            onDrop={handleDrop}
            aria-label="Drop Excel here or click to browse"
          >
            <CloudUploadOutlinedIcon className="customers-import-drop-icon" aria-hidden />
            <span className="customers-import-drop-title">Drop Excel file here</span>
            <span className="customers-import-drop-hint">or browse files</span>
          </div>

          {fileName && (
            <div className="customers-import-file-row">
              <InsertDriveFileOutlinedIcon className="customers-import-file-icon" fontSize="small" />
              <span className="customers-import-file-name">{fileName}</span>
              <button
                type="button"
                className="customers-import-file-clear"
                disabled={isUploading}
                onClick={() => {
                  setFile(null);
                  setFileName('');
                  setError('');
                }}
              >
                Remove
              </button>
            </div>
          )}

          {error && <div className="customers-import-error">{error}</div>}

          <Button
            type="button"
            variant="contained"
            size="large"
            disabled={!file || isUploading}
            onClick={handleUpload}
            sx={{ mt: 2, alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600, px: 3, py: 1.25 }}
          >
            {isUploading ? 'Importing…' : 'Import customers'}
          </Button>
        </div>

        {result?.stats && (
          <div className="customers-import-result">
            <h3 className="customers-import-result-title">Import complete</h3>
            <p className="customers-import-result-meta">{result.message}</p>
            <Button
              type="button"
              variant="outlined"
              color="success"
              onClick={() => navigate('/admin/customers/list')}
              sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600 }}
            >
              View customer list
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CustomersImport;
